/**
 * 路線比較ダッシュボード (docs/25 §10-6) 用の、選択した複数路線の経路を地図で比較表示する
 * コンポーネント (2026-07-23 追加、ユーザー要望)。
 *
 * `AreaComparisonMap.tsx` (エリア比較、§10-5) と同じ「スモールマルチプル」方式を踏襲する —
 * 選択した路線同士が地理的に離れている場合に1枚の地図へ重ねると `fitBounds` が過度に
 * ズームアウトしてしまう問題を、エリア比較 (v1.6) で「路線ごとに1枚、その路線だけにフィットした
 * 地図」に変更して解決済みのため、同じ設計を最初から採用する。色分けも同じ `REPORT_PALETTE`
 * (`AreaComparisonBarChart.tsx`) を再利用し、比較表・グラフの色と混同しないよう選択順で割り当てる。
 *
 * `RouteReportPreviewMap.tsx` (単一路線＋検出地点のドーナツ表示、`RouteReportModal` 専用) とは
 * 別実装 — 本コンポーネントは地点のドーナツ表示を持たず、路線の折れ線を表示するだけの単純な地図。
 */
import 'maplibre-gl/dist/maplibre-gl.css';
import maplibregl, { type Map as MaplibreMap } from 'maplibre-gl';
import { useEffect, useRef, useState } from 'react';

const LINE_SOURCE_ID = 'route-comparison-line';
const LINE_LAYER_ID = 'route-comparison-line-stroke';

export interface RouteComparisonMapRoute {
  id: string;
  name: string;
  lineCoordinates: { latitude: number; longitude: number }[];
  color: string;
}

export interface RouteComparisonMapProps {
  routes: RouteComparisonMapRoute[];
  className?: string;
}

/** 1路線分の独立した地図 (自身の折れ線だけに fitBounds する)。 */
function RouteMiniMap({ route }: { route: RouteComparisonMapRoute }) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<MaplibreMap | null>(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    const map = new maplibregl.Map({
      container: containerRef.current,
      style: {
        version: 8,
        sources: {
          gsi: {
            type: 'raster',
            tiles: ['https://cyberjapandata.gsi.go.jp/xyz/pale/{z}/{x}/{y}.png'],
            tileSize: 256,
            maxzoom: 18,
            attribution: '地理院タイル',
          },
        },
        layers: [{ id: 'gsi', type: 'raster', source: 'gsi' }],
      },
      center: [139.767, 35.681],
      zoom: 12,
      attributionControl: false,
    });
    map.addControl(new maplibregl.NavigationControl({ showCompass: false }), 'top-right');
    map.on('load', () => setReady(true));
    mapRef.current = map;
    return () => {
      map.remove();
      mapRef.current = null;
      setReady(false);
    };
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !ready) return;
    if (route.lineCoordinates.length < 2) return;

    const fc: GeoJSON.FeatureCollection = {
      type: 'FeatureCollection',
      features: [
        {
          type: 'Feature',
          properties: {},
          geometry: {
            type: 'LineString',
            coordinates: route.lineCoordinates.map((c) => [c.longitude, c.latitude]),
          },
        },
      ],
    };

    if (!map.getSource(LINE_SOURCE_ID)) {
      map.addSource(LINE_SOURCE_ID, { type: 'geojson', data: fc });
      map.addLayer({
        id: LINE_LAYER_ID,
        type: 'line',
        source: LINE_SOURCE_ID,
        layout: { 'line-cap': 'round', 'line-join': 'round' },
        paint: { 'line-color': route.color, 'line-width': 4 },
      });
    } else {
      (map.getSource(LINE_SOURCE_ID) as maplibregl.GeoJSONSource).setData(fc);
      map.setPaintProperty(LINE_LAYER_ID, 'line-color', route.color);
    }

    const bounds = new maplibregl.LngLatBounds();
    for (const c of route.lineCoordinates) bounds.extend([c.longitude, c.latitude]);
    map.fitBounds(bounds, { padding: 24, maxZoom: 18, duration: 0 });
  }, [ready, route]);

  return (
    <div>
      <p className="flex items-center gap-1.5 text-xs font-semibold text-slate-700 mb-1">
        <span
          className="w-2.5 h-2.5 rounded-full shrink-0"
          style={{ backgroundColor: route.color }}
        />
        {route.name}
      </p>
      <div ref={containerRef} className="w-full h-48 rounded border border-slate-200" />
    </div>
  );
}

export function RouteComparisonMap({ routes, className }: RouteComparisonMapProps) {
  const validRoutes = routes.filter((r) => r.lineCoordinates.length >= 2);
  if (validRoutes.length === 0) {
    return <p className="text-sm text-slate-400">表示できる路線がありません。</p>;
  }
  return (
    <div className={className ?? 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4'}>
      {validRoutes.map((r) => (
        <RouteMiniMap key={r.id} route={r} />
      ))}
    </div>
  );
}
